import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { archiveRequest } from '../../../shared/api/requests.api'
import { ErrorMessage } from '../../../shared/ui/ErrorMessage'
import { ARCHIVE_REASON_CODES, ARCHIVE_REASON_LABELS, requiresComment } from '@crm/shared'

type Props = {
  requestId: string
  onClose: () => void
  onArchived: () => void
}

export function ArchiveRequestModal({ requestId, onClose, onArchived }: Props) {
  // Причина архивации — выбор из списка плюс комментарий (19.09.2026):
  // свободный текст в базе разрастался в десятки формулировок одного и того же.
  const [code, setCode] = useState('')
  const [reason, setReason] = useState('')
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: () => archiveRequest(requestId, reason.trim(), code),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['requests'] })
      await queryClient.invalidateQueries({ queryKey: ['requests', requestId] })
      onArchived()
    },
  })

  const commentRequired = !code || requiresComment(code)
  const canSubmit = !!code && (!commentRequired || reason.trim().length > 0) && !mutation.isPending

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 text-base font-semibold text-gray-900">Архивировать заявку</div>
        <p className="mb-3 text-sm text-gray-500">
          Заявка переедет в раздел «Обработанные заявки» с пометкой «В архиве». Причина попадёт в
          аналитику, комментарий сохранится в карточке.
        </p>

        <label className="mb-1 block text-xs font-medium text-gray-600">Причина архивации *</label>
        <div className="mb-3 space-y-1">
          {ARCHIVE_REASON_CODES.map((c) => (
            <label key={c} className="flex items-center gap-2 text-sm text-gray-800">
              <input
                type="radio"
                name="archive-reason"
                value={c}
                checked={code === c}
                onChange={() => setCode(c)}
                className="text-brand-600"
              />
              {ARCHIVE_REASON_LABELS[c]}
            </label>
          ))}
        </div>

        <label className="mb-1 block text-xs font-medium text-gray-600">
          Комментарий{commentRequired ? ' *' : ''}
        </label>
        <textarea
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, 2000))}
          rows={4}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          placeholder={
            commentRequired
              ? 'Комментарий обязателен: опишите, почему заявка уходит в архив'
              : 'Комментарий (необязательно)'
          }
        />
        <div className="mt-1 text-right text-[11px] text-gray-400">{reason.length}/2000</div>

        {mutation.error != null && (
          <div className="mt-2">
            <ErrorMessage error={mutation.error} />
          </div>
        )}

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors disabled:opacity-50"
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={!canSubmit}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 transition-colors disabled:opacity-50"
          >
            {mutation.isPending ? 'Архивирую…' : 'В архив'}
          </button>
        </div>
      </div>
    </div>
  )
}
